import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import ReactJson from "react-json-view";
import debounce from "lodash.debounce";

import {
    fetchAccount,
    fetchAccountStopLoss,
    fetchAccountStopLossProgress,
    fetchBenefitPlanDetail,
    fetchBenefitPlans,
    getAccountsByParentAccountKeyAndAccountLevel,
} from "../../utils/apiUtils";
import HrpDate from "../shared/HrpDate";
import Modal from "../modal/Modal";
import { money, toIsoDate } from "../finance/financeShared";

const PAGE_LIMIT = 20;

// Errors thrown by fetchUtils sometimes carry the literal string
// "[object Object]" as their message (when the backend returns a JSON error
// whose `message` field is itself an object). Fall back to the cause or a
// JSON dump so the UI never shows that placeholder.
const formatErrorMessage = (error) => {
    const msg = error?.response?.data?.message ?? error?.message;
    if (typeof msg === "string" && msg !== "[object Object]") return msg;
    if (error?.cause) {
        try { return typeof error.cause === "string" ? error.cause : JSON.stringify(error.cause); }
        catch { /* ignore */ }
    }
    return msg ?? "Request failed";
};

const loadHrpStopLossForAccount = async (row) => {
    if (row?.accountKey == null) {
        return { stopLoss: null, progress: null, stopLossError: "This account has no account key." };
    }
    const query = { accountKey: row.accountKey, asOfDate: toIsoDate(new Date()) };
    try {
        const [stopLoss, progress] = await Promise.all([
            fetchAccountStopLoss(query),
            fetchAccountStopLossProgress(query).catch(() => null),
        ]);
        return { stopLoss, progress, stopLossError: null };
    } catch (err) {
        return { stopLoss: null, progress: null, stopLossError: formatErrorMessage(err) };
    }
};

const Fact = ({ label, value }) => {
    if (value == null || value === "" || value === "—") return null;
    return (
        <div style={{ minWidth: "140px" }}>
            <p style={{ fontSize: ".75rem", color: "var(--grey)", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: ".2rem" }}>
                {label}
            </p>
            <p style={{ fontSize: ".95rem", fontWeight: 500 }}>{value}</p>
        </div>
    );
};

const HrpAccountStopLoss = ({ stopLoss, progress, error }) => {
    const primary = stopLoss?.primary;
    const terms = primary?.stopLoss;
    const reinsurer = primary?.reinsurer;

    return (
        <div style={{
            backgroundColor: "var(--white)",
            borderRadius: ".6rem",
            boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1px",
            padding: "1rem 1.2rem",
        }}>
            <p style={{ fontSize: "1rem", fontWeight: 600, paddingBottom: ".6rem", color: "var(--darkerGrey)" }}>
                HRP Stop Loss
            </p>
            {error && <p className="danger">{error}</p>}
            {!error && !primary && (
                <p style={{ color: "var(--grey)" }}>No HRP stop-loss / reinsurer contract is on this account.</p>
            )}
            {primary && (
                <>
                    <p style={{ fontSize: "1.05rem", fontWeight: 600, paddingBottom: ".3rem" }}>
                        {primary.contractName || "Stop-loss contract"}
                        {primary.active ? " · Active" : ""}
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem 1.6rem", padding: ".4rem 0 1rem" }}>
                        <Fact label="Type" value={primary.contractType} />
                        <Fact label="Contract HCC ID" value={primary.contractHccId} />
                        <Fact label="Status" value={primary.contractStatus} />
                        <Fact label="Start" value={primary.startDate} />
                        <Fact label="End" value={primary.endDate} />
                        <Fact label="Reinsurer" value={reinsurer?.name} />
                        <Fact label="Reinsurer HCC ID" value={reinsurer?.hccId} />
                        <Fact label="Contact" value={reinsurer?.contactName} />
                        <Fact label="Phone" value={reinsurer?.phone} />
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem 1.6rem", paddingBottom: progress ? "1rem" : 0 }}>
                        <Fact label="Specific deductible" value={money(terms?.deductible)} />
                        <Fact label="Specific amount" value={money(terms?.amount)} />
                        <Fact label="Aggregate cutoff" value={money(terms?.cutoff)} />
                        <Fact label="Notification" value={money(terms?.notificationAmount)} />
                        {terms?.notificationPercent != null && (
                            <Fact label="Notification %" value={`${terms.notificationPercent}%`} />
                        )}
                    </div>
                </>
            )}
            {progress?.contractName && (
                <div style={{ borderTop: "1px solid var(--lightestGrey)", paddingTop: "1rem" }}>
                    <p style={{ fontSize: ".9rem", fontWeight: 600, paddingBottom: ".5rem" }}>Progress</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem 1.6rem" }}>
                        <Fact label="Aggregate accumulated" value={money(progress.aggregateAccumulated)} />
                        <Fact label="Aggregate remaining" value={money(progress.aggregateRemaining)} />
                        <Fact label="Aggregate over by" value={money(progress.aggregateOverBy)} />
                        <Fact label="Aggregate status" value={progress.aggregateStatus} />
                        <Fact label="Members at specific" value={progress.membersHitCount} />
                    </div>
                </div>
            )}
            {stopLoss?.contracts?.length > 1 && (
                <p className="sub-title" style={{ paddingTop: "1rem" }}>
                    {stopLoss.contracts.length} contract periods on this account
                    {stopLoss.activeCount ? ` · ${stopLoss.activeCount} active` : ""}.
                </p>
            )}
        </div>
    );
};

// Drill-down view of the HRP account tree. Top accounts are shown first;
// clicking a row's `subAccountsCount` cell descends one level by setting
// `accountLevel` and `parentAccountKey` in the URL search params.
const AccountHierarchy = () => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    // Tracks which row+action is currently fetching so we can render an inline
    // spinner in the clicked cell instead of leaving the user with no feedback.
    const [loadingCell, setLoadingCell] = useState(null); // { key, action: 'details' | 'plans' }

    // Breadcrumb trail: ancestors the user clicked through to reach the
    // current view. Each entry is the row we drilled INTO; the current view
    // shows that row's children. Lives in component state because the
    // hierarchy API only returns rows for the requested level — we have no
    // way to recover ancestor names from the URL alone after a reload.
    const [trail, setTrail] = useState([]);

    const [searchParams, setSearchParams] = useSearchParams();

    // The defaults passed to useSearchParams are NOT written to the URL, so
    // we have to fall back here. accountLevel must be a real number because
    // the fetch util strips null/undefined query params before sending.
    const accountLevel = Number(searchParams.get("accountLevel") ?? 0);
    const parentAccountKey = searchParams.get("parentAccountKey");

    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
        status,
    } = useInfiniteQuery({
        queryKey: ["accountHierarchy", accountLevel, parentAccountKey],
        queryFn: ({ pageParam }) =>
            getAccountsByParentAccountKeyAndAccountLevel({
                ...pageParam,
                limit: PAGE_LIMIT,
                accountLevel,
                parentAccountKey,
            }),
        select: (data) => data.pages.flatMap((page) => page.rows),
        initialPageParam: 0,
        // A short last page (or empty page) signals there are no more rows.
        // Without this, hasNextPage stays true forever and we'd loop the API.
        getNextPageParam: (lastPage, pages) => {
            const len = lastPage?.rows?.length ?? 0;
            if (len < PAGE_LIMIT) return undefined;
            return {
                offset: pages.reduce((accum, page) => accum + page.rows.length, 0),
            };
        },
        staleTime: 0,
        enabled: true,
    });

    const { ref, inView } = useInView({
        threshold: 0.1,
        skip: isFetching || isLoading || !hasNextPage,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetching) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetching, fetchNextPage]);

    const debouncedSearchHandler = useCallback(
        debounce((value) => setDebouncedSearch(value.trim().toLowerCase()), 300),
        []
    );

    // Client-side filter over loaded rows. The hierarchy endpoint doesn't
    // support name/id search server-side, so we filter what we have and rely
    // on infinite scroll to surface more rows.
    const filteredRows = useMemo(() => {
        if (!debouncedSearch) return data ?? [];
        return (data ?? []).filter((row) =>
            [row.accountHccId, row.accountName, row.accountKey]
                .some((v) => String(v ?? "").toLowerCase().includes(debouncedSearch))
        );
    }, [data, debouncedSearch]);

    // The trail is supplementary breadcrumb info; the URL is the source of
    // truth for what's displayed. We DON'T auto-reset trail in an effect when
    // the URL changes — setTrail (React state) and setSearchParams (react-
    // router store) aren't always batched into a single render, so an effect
    // watching URL params can fire on the URL-updated render before the trail
    // update lands, see an empty `last`, and wipe the trail. Instead we check
    // alignment at render time (see `trailAligned` below).

    // The crumb stores the level we were VIEWING when the user drilled in
    // (= the URL's accountLevel at click time). Using URL level rather than
    // `row.accountLevel` avoids assumptions about whether top accounts are
    // 0- or 1-indexed in the API response.
    const drillInto = (row) => {
        setSearchValue("");
        setDebouncedSearch("");
        const viewLevel = accountLevel;
        setTrail((prev) => [
            ...prev,
            {
                accountKey: row.accountKey,
                accountName: row.accountName,
                viewLevel,
            },
        ]);
        setSearchParams({
            accountLevel: viewLevel + 1,
            parentAccountKey: row.accountKey,
        });
    };

    // idx === -1 means Home (top-level). Otherwise navigate to the children
    // of trail[idx], slicing the trail back to that point.
    const goToCrumb = (idx) => {
        setSearchValue("");
        setDebouncedSearch("");
        if (idx === -1) {
            setTrail([]);
            setSearchParams({});
            return;
        }
        const newTrail = trail.slice(0, idx + 1);
        const parent = newTrail[newTrail.length - 1];
        setTrail(newTrail);
        setSearchParams({
            accountLevel: parent.viewLevel + 1,
            parentAccountKey: parent.accountKey,
        });
    };

    // Trail is only meaningful when its last entry corresponds to the parent
    // we're currently viewing the children of. On a deep-link / page reload
    // the trail starts empty, so we fall back to a level label.
    // NOTE: `searchParams.get` always returns strings, but `row.accountKey`
    // from the API is a number — compare as strings so the trail isn't
    // mistakenly treated as desynced after every drill-down.
    const trailAligned =
        trail.length > 0 &&
        String(trail[trail.length - 1].accountKey) === String(parentAccountKey);
    const effectiveTrail = trailAligned ? trail : [];

    const isAtHome = accountLevel === 0 && !parentAccountKey;

    const showAccountDetails = async (row) => {
        if (loadingCell) return;
        setLoadingCell({ key: row.accountKey, action: "details" });
        try {
            const accountPromise = fetchAccount({
                accountIdentifier: { hccIdentificationNumber: row.accountHccId },
            });
            const stopLossInfo = await loadHrpStopLossForAccount(row);
            const result = await accountPromise;
            setSelectedItem({
                kind: "account",
                data: result,
                accountName: row.accountName,
                accountHccId: row.accountHccId,
                ...stopLossInfo,
            });
        } catch (err) {
            alert(err.message);
        } finally {
            setLoadingCell(null);
        }
    };

    const showBenefitPlansForTopAccount = async (row) => {
        if (loadingCell) return;
        setLoadingCell({ key: row.accountKey, action: "plans" });
        try {
            const result = await fetchBenefitPlanDetail({
                accountCriteria: {
                    topAccountIdentifier: { hccIdentificationNumber: row.accountHccId },
                },
            });
            setSelectedItem({ kind: "benefitplan", data: result });
        } catch (err) {
            alert(err.message);
        } finally {
            setLoadingCell(null);
        }
    };

    const isCellLoading = (row, action) =>
        loadingCell?.key === row.accountKey && loadingCell?.action === action;

    // The hierarchy API doesn't return versionExpDateKey, so we can't reuse
    // the SharedTable/Row.jsx check verbatim. Instead, an account row is the
    // "current" version when both:
    //   - accountStatus is active ("A"/"a"), AND
    //   - accountTerminationDateKey is one of HRP's "not set" sentinels
    //     (-9223372036854776000 → 01/01/1800, 9223372036854776000 → 01/01/3000)
    // Anything else (status "T"/"I"/"X" or a real termination date) is an
    // older/terminated version and gets tinted.
    //
    // Note: accountTerminationDateKey is a surrogate key resolved server-side
    // via getDateByDateKey — it is NOT a millisecond timestamp, so we can only
    // compare it to the known sentinels, not to Date.now().
    const TERMINATION_SENTINELS = new Set([
        "-9223372036854776000",
        "9223372036854776000",
    ]);
    const isOlderVersionRow = (row) => {
        if (!row) return false;
        const status = row.accountStatus ? String(row.accountStatus).toUpperCase() : "";
        if (status && status !== "A") return true;
        const termKey = row.accountTerminationDateKey;
        if (termKey != null && !TERMINATION_SENTINELS.has(String(termKey))) return true;
        return false;
    };

    return (
        <div className={"container "}>
            <Modal
                isOpen={!!selectedItem}
                onClose={() => setSelectedItem(null)}
            >
                <div style={{ padding: "1rem", width: "100%", height: "100%", overflowY: "scroll" }}>
                    {selectedItem?.kind === "benefitplan" ? (
                        <BenefitPlansTable
                            data={selectedItem.data}
                            // Stash the current benefit-plan list as `parent` so the
                            // JSON detail view can offer a "back" link to return to it
                            // without re-fetching.
                            onSelect={(plan) =>
                                setSelectedItem({ kind: "json", data: plan, parent: selectedItem })
                            }
                        />
                    ) : (
                        <>
                            {selectedItem?.parent && (
                                <span
                                    className="clickable-link"
                                    onClick={() => setSelectedItem(selectedItem.parent)}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "0.4rem",
                                        marginBottom: "1rem",
                                        fontSize: "0.95rem",
                                    }}
                                    aria-label="Back to benefit plans"
                                >
                                    <span aria-hidden="true">←</span> Back to plans
                                </span>
                            )}
                            {selectedItem?.kind === "account" && (
                                <div style={{ padding: "0 1rem 1rem" }}>
                                    <p className="title">{selectedItem.accountName || "Account"}</p>
                                    <p className="sub-title">HCC {selectedItem.accountHccId}</p>
                                    <div style={{ paddingTop: "1rem" }}>
                                        <HrpAccountStopLoss
                                            stopLoss={selectedItem.stopLoss}
                                            progress={selectedItem.progress}
                                            error={selectedItem.stopLossError}
                                        />
                                    </div>
                                </div>
                            )}
                            <ReactJson
                                style={{ padding: "1rem" }}
                                src={selectedItem?.data || {}}
                                name={false}
                                collapsed={2}
                                collapseStringsAfterLength={150}
                                displayDataTypes={false}
                                displayObjectSize={false}
                                sortKeys={true}
                            />
                        </>
                    )}
                </div>
            </Modal>

            <p className="title">Account Hierarchy</p>
            <p className="sub-title">Drill down through the HRP account tree</p>

            <Breadcrumb
                isAtHome={isAtHome}
                trail={effectiveTrail}
                accountLevel={accountLevel}
                parentAccountKey={parentAccountKey}
                onNavigate={goToCrumb}
            />

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                    padding: "1rem 1rem 1rem 0rem",
                }}
            >
                <input
                    style={{
                        fontSize: "1rem",
                        minWidth: "250px",
                        padding: "1rem",
                        borderRadius: ".4rem",
                        border: "1px solid var(--lighterGrey)",
                        outline: "none",
                    }}
                    type="text"
                    placeholder="Search HCC ID, name, or key..."
                    autoComplete="off"
                    value={searchValue}
                    onChange={(e) => {
                        setSearchValue(e.target.value);
                        debouncedSearchHandler(e.target.value);
                    }}
                />
            </div>

            {isLoading ? (
                <div className="loader" />
            ) : (
                <>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>HCC ID</th>
                                    <th>Account Key</th>
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th>Subscribers</th>
                                    <th>First Effective</th>
                                    <th>Effective</th>
                                    <th>Termination</th>
                                    <th>Sub-Accounts</th>
                                    <th>Plans</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.length ? filteredRows.map((row, i) => (
                                    <tr
                                        key={`${row.accountKey}-${i}`}
                                        style={{ backgroundColor: isOlderVersionRow(row) ? "var(--lightRed)" : "" }}
                                    >
                                        <td>{i + 1}</td>
                                        {isCellLoading(row, "details") ? (
                                            <td className="td-loader" />
                                        ) : (
                                            <td
                                                className="clickable-link"
                                                onClick={() => showAccountDetails(row)}
                                            >
                                                {row.accountHccId}
                                            </td>
                                        )}
                                        <td>{row.accountKey}</td>
                                        <td>{row.accountName}</td>
                                        <td>{row.accountStatus}</td>
                                        <td>{row.canHaveSubscr}</td>

                                        <td><HrpDate dateKey={row.firstEffectiveDateKey} /></td>
                                        <td><HrpDate dateKey={row.accountEffectiveDateKey} /></td>
                                        <td><HrpDate dateKey={row.accountTerminationDateKey} /></td>

                                        <td
                                            className={row.subAccountsCount ? "clickable-link" : ""}
                                            onClick={() => row.subAccountsCount && drillInto(row)}
                                        >
                                            {row.subAccountsCount || "—"}
                                        </td>

                                        {isCellLoading(row, "plans") ? (
                                            <td className="td-loader" />
                                        ) : (
                                            <td
                                                className={!row.accountLevel ? "clickable-link" : ""}
                                                onClick={() => {
                                                    if (!row.accountLevel) {
                                                        showBenefitPlansForTopAccount(row);
                                                    }
                                                }}
                                            >
                                                {!row.accountLevel ? "View" : "—"}
                                            </td>
                                        )}
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={11} style={{ textAlign: "center", padding: "1.5rem", opacity: 0.6 }}>
                                            {debouncedSearch
                                                ? `No matches for "${debouncedSearch}" in loaded rows.`
                                                : "No accounts at this level."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/*
                            Sentinel must live INSIDE .table-wrapper because the
                            wrapper is the scroll container; if it lived outside,
                            scrolling within the table would never bring it into
                            the viewport and infinite scroll would never fire.
                            Once the list is exhausted we drop it entirely so we
                            don't leave a blank padded strip at the bottom.
                        */}
                        {(hasNextPage || isFetchingNextPage) && (
                            <div
                                ref={ref}
                                style={{ padding: "0.75rem", textAlign: "center", opacity: 0.6 }}
                            >
                                {isFetchingNextPage ? "Loading more..." : "Scroll for more"}
                            </div>
                        )}
                    </div>

                    {status === "error" ? (
                        <p className="danger">{formatErrorMessage(error)}</p>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default AccountHierarchy;

// Breadcrumb showing the trail of accounts the user drilled through.
// "Home" navigates back to the top level; each ancestor crumb slices the
// trail back to that point. The last crumb represents the current view and
// is non-interactive. If the trail is empty but the URL implies we're not
// at the top (deep link / page reload), fall back to a level/parent label.
const Breadcrumb = ({ isAtHome, trail, accountLevel, parentAccountKey, onNavigate }) => {
    return (
        <nav
            aria-label="Account hierarchy breadcrumb"
            style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                flexWrap: "wrap",
                padding: "0.5rem 0",
                fontSize: "0.95rem",
            }}
        >
            <Crumb
                label="Home"
                isCurrent={isAtHome}
                onClick={isAtHome ? undefined : () => onNavigate(-1)}
            />

            {trail.map((step, i) => {
                const isLast = i === trail.length - 1;
                return (
                    <Fragment key={`${step.accountKey}-${i}`}>
                        <Separator />
                        <Crumb
                            label={step.accountName || step.accountKey}
                            isCurrent={isLast}
                            onClick={isLast ? undefined : () => onNavigate(i)}
                        />
                    </Fragment>
                );
            })}

            {trail.length === 0 && !isAtHome && (
                <>
                    <Separator />
                    <Crumb
                        label={`Level ${accountLevel}${parentAccountKey ? ` · parent ${parentAccountKey}` : ""}`}
                        isCurrent
                    />
                </>
            )}
        </nav>
    );
};

const Crumb = ({ label, isCurrent, onClick }) => (
    <span
        onClick={onClick}
        className={onClick ? "clickable-link" : ""}
        style={{
            cursor: onClick ? "pointer" : "default",
            color: isCurrent ? "inherit" : "var(--skyDark)",
            fontWeight: isCurrent ? 600 : 400,
            maxWidth: "20rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        }}
        title={label}
    >
        {label}
    </span>
);

const Separator = () => (
    <span style={{ opacity: 0.4, userSelect: "none" }}>›</span>
);

// Benefit-plan rollup for a top account. Clicking a row opens its detail JSON
// in the same modal (the internal app's deep-link to a benefitplan-detail page
// has no equivalent route in this app, so we fall back to a JSON view).
const BenefitPlansTable = ({ data, onSelect }) => {
    const [loadingPlanKey, setLoadingPlanKey] = useState(null);

    return (
        <div>
            <p className="title">Benefit Plans</p>
            <p className="sub-title">Plans associated with this top account</p>
            <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>HCC ID</th>
                            <th>Plan Identifier</th>
                            <th>Plan Name</th>
                            <th>Template Identifier</th>
                            <th>Template Name</th>
                            <th>Effective Start</th>
                            <th>Effective End</th>
                            <th>Product Identifier</th>
                            <th>Product Name</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.items
                            ?.filter((i) => i)
                            .map((row) => (
                                <tr key={row.benefitPlanIdentifier}>
                                    {loadingPlanKey === row.benefitPlanIdentifier ? (
                                        <td className="td-loader" />
                                    ) : (
                                        <td
                                            className="clickable-link"
                                            onClick={async () => {
                                                if (loadingPlanKey) return;
                                                setLoadingPlanKey(row.benefitPlanIdentifier);
                                                try {
                                                    const dbresult = await fetchBenefitPlans({ benefitPlanHccId: row.benefitPlanHccId });
                                                    onSelect({ ...row, dbRow: dbresult?.rows?.[0] });
                                                } catch (err) {
                                                    alert(err.message);
                                                } finally {
                                                    setLoadingPlanKey(null);
                                                }
                                            }}
                                        >
                                            {row.benefitPlanHccId}
                                        </td>
                                    )}
                                    <td>{row.benefitPlanIdentifier}</td>
                                    <td>{row.benefitPlanName}</td>
                                    <td>{row.benefitTemplateIdentifier}</td>
                                    <td>{row.benefitTemplateName}</td>
                                    <td>{row.effectiveStartDate}</td>
                                    <td>{row.effectiveEndDate}</td>
                                    <td>{row.productIdentifier}</td>
                                    <td>{row.productName}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
